// The email that sends the application form link to a client (SEP-289,
// SEP-378). The IM or the EA edits it in Septima, reads the preview, and
// Septima sends it from their own mailbox.
//
// The message is plain text. It holds the marker `{link}` where the link goes,
// because Septima makes the link only when the email is sent. One renderer
// gives the preview and the sent email, so the two cannot differ.

/** The place of the link in the message. */
export const APPLICATION_FORM_LINK_MARKER = '{link}';

/** The days that an invitation link stays open. The server holds the same value. */
export const APPLICATION_FORM_LINK_MAX_AGE_DAYS = 30;

/** The first subject and message of the email. The sender can change both. */
export function applicationFormInvitationTemplate(input: {
    forenames: string;
    senderName: string;
}): { subject: string; message: string } {
    return {
        subject: 'Your application to Dowgate',
        message: [
            `Dear ${input.forenames},`,
            '',
            'Thank you for meeting us. Please complete your application form online. Open the link below, choose a password, then fill in the form. You can save your answers and continue later.',
            '',
            APPLICATION_FORM_LINK_MARKER,
            '',
            `The link works one time and expires in ${APPLICATION_FORM_LINK_MAX_AGE_DAYS} days.`,
            '',
            'Kind regards,',
            input.senderName,
            'Dowgate',
        ].join('\n'),
    };
}

/** True when the message holds the link marker. A message with no link is refused. */
export function hasApplicationFormLinkMarker(message: string): boolean {
    return message.includes(APPLICATION_FORM_LINK_MARKER);
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * The HTML body of the email. A blank line starts a paragraph, a line break
 * stays a line break, and each marker becomes the link.
 */
export function applicationFormInvitationHtml(message: string, link: string): string {
    const anchor = `<a href="${escapeHtml(link)}">${escapeHtml(link)}</a>`;
    return message
        .replace(/\r\n/g, '\n')
        .trim()
        .split(/\n{2,}/)
        .map(
            (paragraph) =>
                `<p>${paragraph
                    .split('\n')
                    .map((line) =>
                        line.split(APPLICATION_FORM_LINK_MARKER).map(escapeHtml).join(anchor),
                    )
                    .join('<br>')}</p>`,
        )
        .join('\n');
}
